"use client";

import { ReactNode, useState } from 'react';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Footer } from '../../components/layout/Footer';
import { AuthButtons } from '../../components/auth/auth-buttons';
import { useSubscription } from '../../hooks/use-subscription';
import { ThemeToggle } from '../../components/ui/theme-toggle';
import { Attribution } from '../../components/layout/Attribution';

interface HomeNavItem {
  label: string;
  href: string;
}

const homeNavItems: HomeNavItem[] = [
  { label: 'Home', href: '/home' },
  { label: 'Features', href: '#features' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Pricing', href: '#pricing' },
];

function HomeHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme } = useTheme();
  const { hasSubscription } = useSubscription();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white/95 backdrop-blur dark:border-gray-800 dark:bg-gray-950/95">
      <div className="w-full flex h-16 items-center px-6 md:px-8 lg:px-12">
        {/* Logo/Brand and Navigation in one group */}
        <div className="flex items-center flex-1">
          <Link href="/" className="flex items-center space-x-2 mr-12">
            <span className="text-xl font-bold">TabLab</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {homeNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
        
        {/* Auth Buttons and Get Started - Far Right */}
        <div className="hidden md:flex items-center space-x-4">
          {/* Theme Toggle */}
          <div className="flex items-center justify-center h-9">
            <ThemeToggle className="flex items-center justify-center" />
          </div>
          
          {/* Auth Buttons */}
          <AuthButtons 
            signInVariant="ghost" 
            signUpVariant="outline" 
            showGoogleSignIn={true}
          />
          
          {/* Get Started Button or Go to App Button */}
          <Link
            href={hasSubscription ? "/app" : "#pricing"}
            className="inline-flex h-9 items-center justify-center rounded-md bg-supernova-cyan px-4 py-2 text-sm font-medium text-white shadow transition-colors hover:bg-supernova-cyan/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-supernova-cyan"
          >
            {hasSubscription ? "Go to App" : "Get Started"}
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white md:hidden"
        >
          {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden">
          <div className="container mx-auto px-4 py-4 space-y-4">
            {homeNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="block py-2 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Theme Toggle for Mobile */}
            <div className="py-2 flex items-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Theme:</span>
              <div className="flex items-center justify-center">
                <ThemeToggle />
              </div>
            </div>
            
            {/* Auth Buttons for Mobile */}
            <div className="py-2">
              <AuthButtons 
                signInVariant="ghost" 
                signUpVariant="outline" 
                showGoogleSignIn={true}
              />
            </div>
            
            {/* Get Started Button or Go to App Button */}
            <Link
              href={hasSubscription ? "/app" : "#pricing"}
              className="block w-full rounded-md bg-supernova-cyan px-4 py-2 text-center text-sm font-medium text-white shadow transition-colors hover:bg-supernova-cyan/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-supernova-cyan"
              onClick={() => setMobileMenuOpen(false)}
            >
              {hasSubscription ? "Go to App" : "Get Started"}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export default function HomeLayout({
  children,
}: Readonly<{
  children: ReactNode;
}>) {
  return (
    <div className="flex min-h-screen flex-col">
      <HomeHeader />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
      <Attribution />
    </div>
  );
} 